import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { ArrowRight, Check, ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { Headline } from "./SiteShell";
import { OUTCOMES } from "./siteNav";
import { MOCKS } from "./ExploreMocks";
import { ease } from "./kit";

const DUR = 9000;
const N = OUTCOMES.length;
const nn = (i) => String(i + 1).padStart(2, "0");
const indexFor = (search) => { const q = new URLSearchParams(search).get("explore"); const i = OUTCOMES.findIndex((o) => o.key === q); return i < 0 ? null : i; };

export default function Explore() {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.3 });
  const reduce = useReducedMotion();
  const location = useLocation();
  const [s, setS] = useState(() => ({ i: indexFor(window.location.search) ?? 0, t: 0 }));
  const [paused, setPaused] = useState(() => indexFor(window.location.search) != null);
  const [hover, setHover] = useState(false);
  const running = inView && !paused && !hover && !reduce;

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setS((x) => (x.t + 100 >= DUR ? { i: (x.i + 1) % N, t: 0 } : { i: x.i, t: x.t + 100 })), 100);
    return () => clearInterval(id);
  }, [running]);

  useEffect(() => {
    const i = indexFor(location.search);
    if (i != null) { setS({ i, t: 0 }); setPaused(true); }
    if (location.hash === "#explore") { const t = setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80); return () => clearTimeout(t); }
  }, [location.search, location.hash]);

  const go = (i) => { setS({ i: (i + N) % N, t: 0 }); setPaused(true); };
  const o = OUTCOMES[s.i];
  const stage = useMemo(() => { const M = MOCKS[o.key]; return <M />; }, [o.key]);
  const tabRefs = useRef([]);
  const onKey = (e) => {
    const raw = e.target?.dataset?.i;
    const from = raw === undefined ? s.i : Number(raw);
    const d = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (!d) return;
    e.preventDefault();
    const next = (from + d + N) % N;
    go(next);
    tabRefs.current[next]?.focus();
  };
  const btn = "inline-flex h-8 w-8 items-center justify-center border border-zinc-900/20 text-zinc-700 transition-colors hover:border-zinc-900/60 hover:text-zinc-950";

  return (
    <section id="explore" ref={ref} className="site-paper scroll-mt-14" data-testid="explore">
      <div className="wrap py-16 lg:py-24">
        <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7"><Headline paper index="01" eyebrow="Explore TRAIDE" size="lg" title="Everything you need to *win, run and grow* a trade business." /></div>
          <div className="lg:col-span-5 lg:pb-2">
            <p className="text-[15px] leading-relaxed text-zinc-600 sm:text-[17px]">Six outcomes. One connected record. Pick what matters most and see the actual TRAIDE screen that does it — real product UI, not an illustration.</p>
            <div className="mono-label mt-6" data-testid="explore-status">{nn(s.i)} / {nn(N - 1)} · {o.label}</div>
          </div>
        </div>

        <div role="tablist" aria-label="Explore TRAIDE by outcome" onKeyDown={onKey} className="-mx-[var(--pad)] mt-12 flex snap-x overflow-x-auto border-y border-zinc-900/10 px-[var(--pad)] lg:mx-0 lg:mt-16 lg:grid lg:grid-cols-6 lg:overflow-visible lg:px-0" data-testid="explore-tabs">
          {OUTCOMES.map((x, i) => {
            const on = i === s.i;
            return (
              <button key={x.key} ref={(el) => { tabRefs.current[i] = el; }} data-i={i} role="tab" aria-selected={on} onClick={() => go(i)} data-testid={`explore-tab-${x.key}`}
                className={`relative flex min-w-[212px] shrink-0 snap-start flex-col justify-start border-r border-zinc-900/10 px-5 py-5 text-left transition-colors last:border-r-0 lg:min-w-0 ${on ? "bg-zinc-950" : "hover:bg-zinc-900/[.04]"}`}>
                <div className="flex items-center justify-between"><span className={`font-mono text-[11px] font-medium tracking-[.14em] ${on ? "text-amber-400" : "text-amber-700"}`}>{nn(i)}</span><x.icon size={15} className={on ? "text-amber-400" : "text-zinc-400"} /></div>
                <div className={`display mt-4 text-[17px] leading-tight ${on ? "text-white" : "text-zinc-950"}`}>{x.label}</div>
                <div className={`mt-1 text-[12px] leading-snug ${on ? "text-zinc-400" : "text-zinc-500"}`}>{x.short}</div>
                {on && <span className="absolute inset-x-0 bottom-0 h-[3px] bg-white/10"><span className="absolute inset-y-0 left-0 bg-amber-500 transition-[width] duration-100 ease-linear" style={{ width: `${(s.t / DUR) * 100}%` }} data-testid="explore-progress" /></span>}
              </button>
            );
          })}
        </div>

        <div className="grid border-b border-zinc-900/10 lg:grid-cols-12" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
          <div className="order-2 py-10 lg:order-1 lg:col-span-4 lg:border-r lg:border-zinc-900/10 lg:py-12 lg:pr-12" data-testid="explore-detail">
            <motion.div key={o.key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease }}>
              <div className="mono-label amber">{nn(s.i)} — {o.label}</div>
              <p className="display mt-4 text-[24px] leading-[1.15] text-zinc-950 sm:text-[28px]" data-testid="explore-promise">{o.promise}</p>
              <ol className="mt-8 divide-y divide-zinc-900/10 border-y border-zinc-900/10">
                {o.features.map(([t, c], i) => (
                  <li key={t} className="grid grid-cols-[28px_1fr] items-baseline gap-2 py-3">
                    <span className="font-mono text-[10px] font-medium tracking-[.14em] text-amber-700">0{i + 1}</span>
                    <span><span className="display text-[15px] text-zinc-950">{t}</span><span className="block text-[13px] leading-relaxed text-zinc-600">{c}</span></span>
                  </li>
                ))}
              </ol>
              <div className="mt-6 flex flex-wrap gap-2">{o.proof.map((p) => <span key={p} className="inline-flex items-center gap-1.5 border border-zinc-900/15 px-2.5 py-1 text-[12px] font-medium text-zinc-800"><Check size={11} className="text-emerald-600" /> {p}</span>)}</div>
              <Link to={o.anchor} className="site-link mt-8" data-testid="explore-link">See {o.label.toLowerCase()} in the product <ArrowRight size={14} /></Link>
            </motion.div>
          </div>

          <div className="order-1 min-w-0 py-8 lg:order-2 lg:col-span-8 lg:py-12 lg:pl-12">
            <div className="relative overflow-hidden border border-zinc-900/10 bg-[#EAE8E3]" data-testid="explore-stage">
              <div className="site-grid absolute inset-0" />
              <div className="relative p-3 sm:p-6 lg:p-8">
                <motion.div key={o.key} initial={{ opacity: 0, y: 12, scale: 0.99 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.5, ease }} data-testid={`explore-ui-${o.key}`}>{stage}</motion.div>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 sm:gap-3">
              <button onClick={() => go(s.i - 1)} className={btn} aria-label="Previous outcome" data-testid="explore-prev"><ChevronLeft size={14} /></button>
              <button onClick={() => setPaused((p) => !p)} className={btn} aria-label={paused ? "Play tour" : "Pause tour"} data-testid="explore-toggle">{paused ? <Play size={13} /> : <Pause size={13} />}</button>
              <button onClick={() => go(s.i + 1)} className={btn} aria-label="Next outcome" data-testid="explore-next"><ChevronRight size={14} /></button>
              <span className="truncate font-mono text-[10.5px] uppercase tracking-[.14em] text-zinc-600">{nn(s.i)} / {nn(N - 1)} · {o.label}</span>
              <span className="ml-auto hidden shrink-0 font-mono text-[10px] uppercase tracking-[.14em] text-zinc-400 sm:block" data-testid="explore-play-state">{paused ? "Tour paused" : hover ? "Paused while you look" : "Playing · hover to pause"}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
